import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { useContentOverflow } from '../../../../../hooks'
import { getQaSelector } from '../../../../../utils/common'
import {
  isEventKeyEnter,
  isEventKeySpaceBar,
  preventDefault,
} from '../../../../../utils/events'
import { FloatingTooltip } from '../../../../Floating'
import {
  getListingCoverThumbnail,
  getListingUnitLabel,
} from '../../../../RentalPortalListing'
import { Status } from '../../../../Status'
import Text from '../../../../Text'
import { QA_NAME_LISTING_BOARD_CARD } from '../RentalPortalListingsBoard.constants'

import { useRentalPortalListingsBoardCardActions } from './RentalPortalListingsBoardCard.hooks'
import { getListingBoardCardMeta } from './RentalPortalListingsBoardCard.utils'
import { RentalPortalListingsBoardCardCover } from './RentalPortalListingsBoardCardCover'
import {
  getHasListingBoardCardHead,
  RentalPortalListingsBoardCardHead,
} from './RentalPortalListingsBoardCardHead'

import * as styles from './RentalPortalListingsBoardCard.module.css'

function RentalPortalListingsBoardCard({
  getActions,
  isActive,
  isSelectable,
  isSelected,
  item,
  selectNotAllowedTooltip,
  onCallAction,
  onClick,
  onSelect,
}) {
  const { archived, attention, awaiting_approval: awaitingApproval } = item

  const { t } = useTranslation()

  const unitLabel = getListingUnitLabel({ listing: item, t })

  const { overflow, setContentOverflowRef } = useContentOverflow({
    dep: [unitLabel],
  })

  const { actions, handleClickAction } =
    useRentalPortalListingsBoardCardActions({
      getActions,
      item,
      onCallAction,
    })

  function handleClick(event) {
    onClick?.(item.id, event)
  }

  function handleKeyDown(event) {
    if (
      event.target === event.currentTarget &&
      (isEventKeyEnter(event) || isEventKeySpaceBar(event))
    ) {
      preventDefault(event)

      onClick?.(item.id, event)
    }
  }

  function handleSelect(isChecked) {
    onSelect?.(item, isChecked)
  }

  return (
    <div
      {...getQaSelector({ value: QA_NAME_LISTING_BOARD_CARD })}
      className={classNames(styles.RentalPortalListingsBoardCard, {
        [styles.active]: isActive,
      })}
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <RentalPortalListingsBoardCardCover
        alt={unitLabel}
        coverThumbUrl={getListingCoverThumbnail(item)}
      />
      <div className={styles.body}>
        {getHasListingBoardCardHead({ actions, archived, onSelect }) ? (
          <RentalPortalListingsBoardCardHead
            actions={actions}
            archived={archived}
            isSelectable={isSelectable}
            isSelected={isSelected}
            selectNotAllowedTooltip={selectNotAllowedTooltip}
            onClickAction={handleClickAction}
            onSelect={onSelect ? handleSelect : undefined}
          />
        ) : null}
        <FloatingTooltip content={unitLabel} disabled={!overflow}>
          <Text className={styles.title} ref={setContentOverflowRef}>
            {unitLabel}
          </Text>
        </FloatingTooltip>
        <Text isSecondary className={styles.meta}>
          {getListingBoardCardMeta({ item, t })}
        </Text>
        {awaitingApproval ? (
          <Status
            className={styles.badge}
            text={t('RentalPortal.ListingBoardAwaitingApprovalKey')}
            textColor='var(--color-warning)'
          />
        ) : null}
        {attention?.needs_you ? (
          <span className={styles.needsYou}>
            <span className={styles.marker} />
            {t(
              attention.primary_action?.label ||
                'RentalPortal.ListingBoardNeedsYouKey'
            )}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default RentalPortalListingsBoardCard
